'use client';

import Link from 'next/link';
import { ChevronLeft } from 'lucide-react';

export default function TeamGuidePage() {
  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/docs" className="text-brand hover:text-[#2a6bb0] font-medium">
                  Docs & Guides
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-600">Adding Your Team</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          
          {/* Table of Contents - Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-400 mb-4">On this page</h3>
              <nav className="space-y-3 border-l-2 border-gray-100 pl-4">
                <a href="#overview" className="block text-sm text-gray-600 hover:text-brand transition-colors">Overview</a>
                <a href="#inviting" className="block text-sm text-gray-600 hover:text-brand transition-colors">Inviting Team Members</a>
                <a href="#roles" className="block text-sm text-gray-600 hover:text-brand transition-colors">Roles & Permissions</a>
                <a href="#shared-inbox" className="block text-sm text-gray-600 hover:text-brand transition-colors">Shared Inbox</a>
                <a href="#assignment" className="block text-sm text-gray-600 hover:text-brand transition-colors">Conversation Assignment</a>
                <a href="#team-chat" className="block text-sm text-gray-600 hover:text-brand transition-colors">Team Chat</a>
                <a href="#notes" className="block text-sm text-gray-600 hover:text-brand transition-colors">Internal Notes</a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Adding Your Team</h1>
              <p className="text-xl text-gray-600">
                Learn how to invite team members, manage roles, and collaborate effectively within Voley.
              </p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <span>Estimated time: 5 minutes</span>
              </div>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg max-w-none">
              
              <section id="overview" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Overview</h2>
                <p className="text-gray-600 mb-4">
                  Voley is designed for teams. You can invite your entire agency to collaborate on client conversations, use internal team chat, and keep everyone on the same page.
                </p>
                
                <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Pricing for Additional Users</h4>
                  <p className="text-gray-600 mb-0">
                    Each additional user is <strong>$20/month</strong> and adds <strong>200 message credits per month</strong> to your agency&apos;s pool.
                  </p>
                </div>
              </section>

              <section id="inviting" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Inviting Team Members</h2>
                <p className="text-gray-600 mb-4">
                  To add someone to your Voley account:
                </p>
                <ol className="list-decimal pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Go to <strong>Settings → Team Members</strong></li>
                  <li>Click <strong>Add Member</strong></li>
                  <li>Enter their <strong>first name</strong>, <strong>last name</strong>, and <strong>email address</strong></li>
                  <li>Optionally add their phone number</li>
                  <li>Choose whether to grant <strong>admin privileges</strong> (see Roles below)</li>
                  <li>Click <strong>Send Invitation</strong></li>
                </ol>
                <p className="text-gray-600 mb-4">
                  The team member will receive an email invitation to join your account. They&apos;ll create their own password and can start using Voley immediately.
                </p>

                <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl p-5 mb-6">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Note:</strong> Invitations expire after a short time for security. If an invite expires before the person accepts, you can resend it from the Pending Invitations list.
                  </p>
                </div>
              </section>

              <section id="roles" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Roles & Permissions</h2>
                <p className="text-gray-600 mb-4">
                  Voley has two roles: <strong>Admin</strong> and <strong>Agent</strong>. Most team members should be Agents.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <h4 className="font-semibold text-gray-900 mb-3">Agent (Default)</h4>
                    <p className="text-sm text-gray-600 mb-3">Full access to daily operations:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>✓ Send and receive messages</li>
                      <li>✓ View all conversations</li>
                      <li>✓ Manage contacts</li>
                      <li>✓ Use team chat</li>
                      <li>✓ Add internal notes</li>
                      <li>✓ Assign conversations</li>
                    </ul>
                  </div>
                  <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5">
                    <h4 className="font-semibold text-gray-900 mb-3">Admin</h4>
                    <p className="text-sm text-gray-600 mb-3">Everything Agents can do, plus:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>✓ Invite and remove team members</li>
                      <li>✓ Manage phone numbers</li>
                      <li>✓ Manage billing and payment</li>
                      <li>✓ Configure integrations</li>
                      <li>✓ Access organization settings</li>
                    </ul>
                  </div>
                </div>

                <p className="text-gray-600 mb-4">
                  You can change a team member&apos;s role at any time from Settings → Team Members.
                </p>
              </section>

              <section id="shared-inbox" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Shared Inbox</h2>
                <p className="text-gray-600 mb-4">
                  By default, all team members can see all client conversations. This shared inbox approach ensures nothing falls through the cracks and anyone can help a client when needed.
                </p>
                
                <h4 className="text-lg font-semibold mt-6 mb-3 text-gray-900">Conversation Views</h4>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>All Conversations:</strong> Shows every client conversation for your agency</li>
                  <li><strong>Assigned to Me:</strong> Shows only conversations assigned to you specifically</li>
                  <li><strong>Unassigned:</strong> Shows conversations that haven&apos;t been claimed by anyone yet</li>
                </ul>

                <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl p-5 mb-6">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Pro tip:</strong> Use the &quot;Assigned to Me&quot; view to focus on your own workload, while still having access to all conversations when you need to help a colleague or cover for someone.
                  </p>
                </div>
              </section>

              <section id="assignment" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Conversation Assignment</h2>
                <p className="text-gray-600 mb-4">
                  You can assign conversations to specific team members to clarify ownership and responsibility.
                </p>

                <h4 className="text-lg font-semibold mt-6 mb-3 text-gray-900">Claiming a Conversation</h4>
                <p className="text-gray-600 mb-4">
                  When you open a conversation, you can click <strong>Claim</strong> to assign it to yourself. This lets your team know you&apos;re handling that client.
                </p>

                <h4 className="text-lg font-semibold mt-6 mb-3 text-gray-900">Assigning to Others</h4>
                <p className="text-gray-600 mb-4">
                  You can also assign conversations to other team members. Click the assignment icon in the conversation header and select the team member from the list.
                </p>

                <p className="text-gray-600 mb-4">
                  Multiple team members can be assigned to the same conversation if needed — useful for complex cases or training situations.
                </p>
              </section>

              <section id="team-chat" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Team Chat</h2>
                <p className="text-gray-600 mb-4">
                  Voley includes a built-in team chat for internal communication — no need for a separate Slack or Teams subscription.
                </p>

                <h4 className="text-lg font-semibold mt-6 mb-3 text-gray-900">Features</h4>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Channels:</strong> Create public or private channels for different topics, departments, or projects</li>
                  <li><strong>Direct Messages:</strong> Message any team member privately</li>
                  <li><strong>Online Status:</strong> See who&apos;s currently active in the app</li>
                  <li><strong>Real-time:</strong> Messages appear instantly, just like client conversations</li>
                </ul>

                <p className="text-gray-600 mb-4">
                  Access Team Chat from the main navigation. It&apos;s a separate area from client messaging, so internal conversations stay private.
                </p>

                <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5 mb-6">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>No extra cost:</strong> Team chat messages don&apos;t use message credits — it&apos;s included in your subscription.
                  </p>
                </div>
              </section>

              <section id="notes" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Internal Notes</h2>
                <p className="text-gray-600 mb-4">
                  You can add internal notes to any client conversation. Notes are visible only to your team — clients never see them.
                </p>

                <h4 className="text-lg font-semibold mt-6 mb-3 text-gray-900">When to Use Notes</h4>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Document important context about a client or situation</li>
                  <li>Leave instructions for colleagues who might handle the conversation later</li>
                  <li>Record follow-up reminders or action items</li>
                  <li>Note anything relevant that shouldn&apos;t be in the client-facing conversation</li>
                </ul>

                <p className="text-gray-600 mb-4">
                  To add a note, open a conversation and look for the Notes section in the right panel. Notes include timestamps and show who added them.
                </p>
              </section>

            </div>

            {/* Footer Navigation */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Link 
                  href="/docs"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                  Back to Docs
                </Link>
                <div className="text-sm text-gray-500">
                  Need help? <Link href="/contact" className="text-brand hover:text-[#2a6bb0] font-medium">Contact our team</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
